import {
  INCIDENT_LIST_SORT_KEYS,
  INCIDENT_STATUSES,
  PLAN_SIZE_TIERS,
  SEVERITIES,
  SORT_DIRECTIONS,
  type IncidentListSortKey,
  type IncidentStatus,
  type PlanSizeTier,
  type Severity,
  type SortDirection,
} from '@prerender/diagnostics-api';

export type IncidentListSort = {
  key: IncidentListSortKey;
  direction: SortDirection;
};

export type IncidentListParams = {
  search: string;
  incidentTypes: string[];
  crawlers: string[];
  severities: Severity[];
  statuses: IncidentStatus[];
  planTiers: PlanSizeTier[];
  dateStart: string | null;
  dateEnd: string | null;
  sort: IncidentListSort | null;
  page: number;
};

export const DEFAULT_INCIDENT_LIST_PARAMS: IncidentListParams = {
  search: '',
  incidentTypes: [],
  crawlers: [],
  severities: [],
  statuses: [],
  planTiers: [],
  dateStart: null,
  dateEnd: null,
  sort: null,
  page: 1,
};

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isAmong =
  <T extends string>(vocabulary: readonly T[]) =>
  (value: string): value is T =>
    (vocabulary as readonly string[]).includes(value);

const listOf = <T extends string>(searchParams: URLSearchParams, key: string, vocabulary: readonly T[]): T[] =>
  searchParams.getAll(key).filter(isAmong(vocabulary));

const dayOf = (searchParams: URLSearchParams, key: string): string | null => {
  const value = searchParams.get(key);

  return value !== null && DAY_PATTERN.test(value) ? value : null;
};

const pageOf = (searchParams: URLSearchParams): number => {
  const page = Number(searchParams.get('page'));

  return Number.isInteger(page) && page > 0 ? page : DEFAULT_INCIDENT_LIST_PARAMS.page;
};

const sortOf = (searchParams: URLSearchParams): IncidentListSort | null => {
  const key = searchParams.get('sortBy');
  if (key === null || !isAmong(INCIDENT_LIST_SORT_KEYS)(key)) return null;

  const direction = searchParams.get('sortDirection');

  return { key, direction: direction !== null && isAmong(SORT_DIRECTIONS)(direction) ? direction : 'desc' };
};

export function parseIncidentListParams(searchParams: URLSearchParams): IncidentListParams {
  return {
    search: searchParams.get('search') ?? '',
    incidentTypes: searchParams.getAll('incidentType'),
    crawlers: searchParams.getAll('crawler'),
    severities: listOf(searchParams, 'severity', SEVERITIES),
    statuses: listOf(searchParams, 'status', INCIDENT_STATUSES),
    planTiers: listOf(searchParams, 'planTier', PLAN_SIZE_TIERS),
    dateStart: dayOf(searchParams, 'dateStart'),
    dateEnd: dayOf(searchParams, 'dateEnd'),
    sort: sortOf(searchParams),
    page: pageOf(searchParams),
  };
}

const appendAll = (query: URLSearchParams, key: string, values: readonly string[]): void => {
  values.forEach((value) => query.append(key, value));
};

export function serialiseIncidentListParams(params: IncidentListParams): URLSearchParams {
  const query = new URLSearchParams();

  if (params.search !== '') query.set('search', params.search);
  appendAll(query, 'incidentType', params.incidentTypes);
  appendAll(query, 'crawler', params.crawlers);
  appendAll(query, 'severity', params.severities);
  appendAll(query, 'status', params.statuses);
  appendAll(query, 'planTier', params.planTiers);
  if (params.dateStart !== null) query.set('dateStart', params.dateStart);
  if (params.dateEnd !== null) query.set('dateEnd', params.dateEnd);
  if (params.sort !== null) {
    query.set('sortBy', params.sort.key);
    query.set('sortDirection', params.sort.direction);
  }
  if (params.page !== DEFAULT_INCIDENT_LIST_PARAMS.page) query.set('page', String(params.page));

  return query;
}

export type DateRange = Pick<IncidentListParams, 'dateStart' | 'dateEnd'>;

export function hasInvertedDateRange({ dateStart, dateEnd }: DateRange): boolean {
  return dateStart !== null && dateEnd !== null && dateEnd < dateStart;
}

export function nextSort(current: IncidentListSort | null, key: IncidentListSortKey): IncidentListSort | null {
  if (current === null || current.key !== key) return { key, direction: 'desc' };
  if (current.direction === 'desc') return { key, direction: 'asc' };

  return null;
}
